// components/DeviceManagerModal.js
import { Modal } from './modal.ts';
import { fetchDevices, createDevice, updateDevice } from '../services/api.ts';

export class DeviceManagerModal {
  constructor(siteId) {
    this.siteId = siteId;
    this.modal = new Modal();
  }

  async open(existingDevice = null) {
    const isEdit = !!existingDevice;
    const allDevices = await fetchDevices(this.siteId);

    // Only EMS devices can be controllers
    const emsDevices = allDevices.filter(d => d.role === 'ems');

    // Build EMS dropdown options
    const emsOptions = emsDevices.map(d => `
      <option value="${d._id}" ${existingDevice?.emsControllerId === d._id ? 'selected' : ''}>
        ${d.name} (${d.communication?.modbus?.ipAddress || 'No IP'})
      </option>
    `).join('');

    // Determine current EMS name for display when locked
    const currentEms = existingDevice?.emsControllerId
      ? allDevices.find(d => d._id === existingDevice.emsControllerId)
      : null;

    const html = `
      <div class="modal-content device-manager">
        <div class="modal-header">
          <h3>${isEdit ? 'Edit' : 'Add'} Device</h3>
        </div>
        <div class="modal-body">
          <form id="device-form">
            <input type="hidden" name="siteId" value="${this.siteId}">
            ${isEdit ? `<input type="hidden" name="_id" value="${existingDevice._id}">` : ''}

            <div class="form-grid">
              <!-- Device Name -->
              <div>
                <label>Name *</label>
                <input name="name" value="${existingDevice?.name || ''}" required maxlength="100">
              </div>

              <!-- Role -->
              <div>
                <label>Role *</label>
                <select name="role" required ${isEdit ? 'disabled' : ''}>
                  ${['ems','inverter','bess','meter','plc','sensor','gateway','other'].map(r => `
                    <option value="${r}" ${existingDevice?.role === r ? 'selected' : ''}>
                      ${r.toUpperCase()}
                    </option>
                  `).join('')}
                </select>
                ${isEdit ? '<small style="color:#d73;">Role cannot be changed after creation</small>' : ''}
              </div>

              <!-- Model/Type -->
              <div>
                <label>Model/Type</label>
                <input name="type" placeholder="e.g. sungrow_sh10rt" value="${existingDevice?.type || ''}">
                <small>Used for auto-generating register presets</small>
              </div>

              <!-- IP Address -->
              <div>
                <label>IP Address</label>
                <input name="ipAddress" placeholder="192.168.1.100" value="${existingDevice?.communication?.modbus?.ipAddress || ''}">
              </div>

              <!-- Modbus Settings -->
              <div>
                <label>Port</label>
                <input name="port" type="number" min="1" max="65535" value="${existingDevice?.communication?.modbus?.port || '502'}">
              </div>
              <div>
                <label>Unit ID (Slave ID)</label>
                <input name="unitId" type="number" min="0" max="247" value="${existingDevice?.communication?.modbus?.unitId || '1'}">
              </div>

              <!-- EMS CONTROLLER — SMART LOGIC -->
              <div class="full-width">
                <label>
                  EMS Controller
                  ${existingDevice?.role === 'ems' ? 
                    ' <span style="color:#d73; font-weight:bold;">(EMS Master — cannot have controller)</span>' :
                    existingDevice?.emsControllerId ? 
                      ' <span style="color:#28a745;">(Can change or remove)</span>' :
                      ' <span style="color:#0066cc;">(Optional — direct polling if empty)</span>'
                  }
                </label>

                ${existingDevice?.role === 'ems' ? `
                  <div style="padding: 0.8rem; background:#fff0f0; border-radius:6px; color:#d73;">
                    This device is an EMS Master — it cannot be controlled by another EMS.
                  </div>
                  <input type="hidden" name="emsControllerId" value="">
                ` : `
                  <select name="emsControllerId">
                    <option value="">No EMS Controller (Direct connection)</option>
                    ${emsOptions}
                  </select>
                  ${existingDevice?.emsControllerId ? `
                    <small style="color:#28a745;">
                      Currently controlled by: <strong>${currentEms?.name || 'Unknown'}</strong>
                    </small>
                  ` : `
                    <small>Leave empty for direct Modbus/TCP polling</small>
                  `}
                `}
              </div>

              <!-- MQTT Settings -->
              <div class="full-width">
                <label>MQTT Topic Prefix <small>(optional)</small></label>
                <input
                  name="mqttTopic"
                  placeholder="site/abc123/devices/"
                  value="${existingDevice?.communication?.mqtt?.topic || ''}"
                >
                <small>Base topic for publishing</small>
              </div>

              <div class="full-width">
                <label>MQTT JSON Root Key <small>(optional)</small></label>
                <input
                  name="mqttJsonRootKey"
                  placeholder="e.g. data, payload, values"
                  value="${existingDevice?.communication?.mqtt?.jsonRootKey || ''}"
                >
                <small>
                  Root object key used when publishing JSON payloads.
                  Leave empty to publish flat JSON.
                </small>
              </div>
            </div>

            <div class="form-actions" style="margin-top: 1.5rem;">
              <button type="submit" class="btn-primary">
                ${isEdit ? 'Update Device' : 'Create Device'}
              </button>
              <button type="button" class="btn-secondary modal-close">Cancel</button>
            </div>
          </form>
        </div>
      </div>
    `;

    this.modal.open(html);

    // Form submit
    const form = this.modal.contentEl.querySelector('#device-form');
    form.onsubmit = async (e) => {
      e.preventDefault();
      const formData = new FormData(form);
      const raw = Object.fromEntries(formData);

      // Normalize EMS
      const emsControllerId =
        raw.emsControllerId && raw.emsControllerId !== ""
          ? raw.emsControllerId
          : null;

      // Decide protocol
      const isMqtt =
        raw.role === "ems"
          ? false
          : isEdit
            ? existingDevice.communication?.type === "mqtt"
            : !!raw.mqttTopic;

      const payload = {
        siteId: this.siteId,
        name: raw.name,
        role: isEdit ? existingDevice.role : raw.role,
        type: raw.type || null,
        emsControllerId,

        communication: isMqtt
          ? {
              type: "mqtt",
              mqtt: {
                topic: raw.mqttTopic,
                jsonRootKey: raw.mqttJsonRootKey || null
              }
            }
          : {
              type: "modbus",
              modbus: {
                ipAddress: raw.ipAddress || null,
                port: raw.port ? Number(raw.port) : 502,
                unitId: raw.unitId ? Number(raw.unitId) : 1
              }
            }
      };
      try {
        if (isEdit) {
          await updateDevice(existingDevice._id, payload);
          alert('Device updated successfully!');
        } else {
          await createDevice(payload);
          alert('Device created successfully!');
        }
        this.modal.close();
        // location.reload(); // or better: emit event
      } catch (err) {
        console.error("Device save error:", err);
        alert('Error: ' + (err.message || 'Failed to save device'));
      }
    };

    // If editing and role is locked, show current role as text
    if (isEdit && existingDevice.role) {
      const roleSelect = form.querySelector('[name="role"]');
      if (roleSelect) {
        roleSelect.disabled = true;
        roleSelect.insertAdjacentHTML('afterend', `
          <div style="margin-top:0.5rem; color:#666; font-size:0.9rem;">
            Role is permanent after creation
          </div>
        `);
      }
    }
  }
}